import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Clock4, Loader2 } from 'lucide-react'

import { ApiHttpError, apiErrorMessage, apiFetch } from '@/lib/api'
import {
  SESSION_QUERY_KEY,
  dropUserScopedQueries,
  type SessionUser,
} from '@/lib/auth'
import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Label from '@/components/ui/Label'

const schema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'Hasło musi mieć co najmniej 8 znaków')
      .max(200, 'Hasło jest za długie'),
    confirmNewPassword: z.string().min(1, 'Powtórz hasło'),
  })
  .refine((v) => v.newPassword === v.confirmNewPassword, {
    path: ['confirmNewPassword'],
    message: 'Hasła nie są identyczne',
  })

type FormValues = z.infer<typeof schema>

interface InvitationInfo {
  valid: true
  email: string
  name: string
  expiresAt: string
}

export default function AcceptInvitation() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const qc = useQueryClient()

  const infoQuery = useQuery<InvitationInfo, ApiHttpError>({
    queryKey: ['invitation-info', token],
    queryFn: () =>
      apiFetch<InvitationInfo>(
        `/api/auth/invitation-info?token=${encodeURIComponent(token)}`,
      ),
    enabled: token.length === 64,
    retry: false,
    staleTime: 60_000,
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmNewPassword: '' },
  })

  const accept = useMutation({
    mutationFn: (values: { newPassword: string }) =>
      apiFetch<{ user: SessionUser }>('/api/auth/accept-invitation', {
        method: 'POST',
        body: { token, newPassword: values.newPassword },
      }),
    onSuccess: (data) => {
      // Na tym urządzeniu mógł wcześniej pracować ktoś inny — jego dane z cache
      // nie mogą przeżyć zmiany tożsamości.
      dropUserScopedQueries(qc)
      qc.setQueryData(SESSION_QUERY_KEY, data.user)
      toast.success('Witaj! Hasło ustawione, logowanie zakończone.')
      navigate('/', { replace: true })
    },
  })

  const submitError = accept.error ? apiErrorMessage(accept.error) : null

  // Brak tokena lub zły format
  if (!token || token.length !== 64) {
    return (
      <main className="min-h-[100svh] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[420px] rounded-card border border-border-subtle bg-bg-elevated p-8">
          <Alert variant="error">Link zaproszenia jest niekompletny.</Alert>
          <p className="mt-4 text-sm text-text-muted">
            <Link to="/login" className="underline hover:opacity-80">
              Wróć do logowania
            </Link>
          </p>
        </div>
      </main>
    )
  }

  if (infoQuery.isLoading) {
    return (
      <main className="min-h-[100svh] flex items-center justify-center px-6 py-12">
        <div className="flex items-center text-text-muted">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          <span className="ml-2 text-sm">Sprawdzam zaproszenie…</span>
        </div>
      </main>
    )
  }

  if (infoQuery.error || !infoQuery.data) {
    return (
      <main className="min-h-[100svh] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[420px] rounded-card border border-border-subtle bg-bg-elevated p-8">
          <Alert variant="error">
            Link zaproszenia wygasł lub jest nieprawidłowy. Skontaktuj się z
            administratorem żeby otrzymać nowy.
          </Alert>
          <p className="mt-4 text-sm text-text-muted">
            <Link to="/login" className="underline hover:opacity-80">
              Wróć do logowania
            </Link>
          </p>
        </div>
      </main>
    )
  }

  const info = infoQuery.data

  return (
    <main className="min-h-[100svh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-[420px] rounded-card border border-border-subtle bg-bg-elevated p-8">
        <div className="mb-6 flex flex-col items-center text-center">
          <Clock4 className="mb-2 h-6 w-6 text-brand-accent" aria-hidden />
          <p className="text-xs uppercase tracking-widest text-text-faint">
            Dental Avenue
          </p>
          <h1 className="mt-1 text-2xl font-semibold">Witaj, {info.name}</h1>
          <p className="mt-1 text-xs text-text-muted">{info.email}</p>
          <p className="mt-3 text-sm text-text-muted">
            Ustaw hasło, żeby się zalogować.
          </p>
        </div>

        <form
          onSubmit={form.handleSubmit(({ newPassword }) =>
            accept.mutate({ newPassword }),
          )}
          noValidate
          className="space-y-4"
        >
          <div>
            <Label htmlFor="newPassword">Nowe hasło</Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              autoFocus
              invalid={!!form.formState.errors.newPassword}
              {...form.register('newPassword')}
            />
            {form.formState.errors.newPassword && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.newPassword.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="confirmNewPassword">Powtórz hasło</Label>
            <Input
              id="confirmNewPassword"
              type="password"
              autoComplete="new-password"
              invalid={!!form.formState.errors.confirmNewPassword}
              {...form.register('confirmNewPassword')}
            />
            {form.formState.errors.confirmNewPassword && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.confirmNewPassword.message}
              </p>
            )}
          </div>

          {submitError && <Alert variant="error">{submitError}</Alert>}

          <Button type="submit" loading={accept.isPending} className="w-full">
            Ustaw hasło i zaloguj się
          </Button>
        </form>
      </div>
    </main>
  )
}
